import { Button, type ButtonProps } from '../primitives/button.js'
import { ExpandLess, ExpandMore } from '@tunasong/icons'
import cn from 'classnames'
import type { FC } from 'react'
import { useState } from 'react'
import { useDimensions } from '../layout/dimensions.js'
import { ListItemIcon, Menu, MenuContent, MenuItem, MenuTrigger } from '../primitives/index.js'
import moduleStyles from './menu-button.module.css'

interface MenuButtonItem {
  Icon: FC
  name: string
}

export interface MenuButtonSelector<T extends MenuButtonItem> extends Omit<ButtonProps, 'onChange'> {
  items: T[]
  selected?: T
  /** Show the selected item's icon before its name. Default true. */
  showIcon?: boolean
  /** Accessible name of the trigger. Defaults to 'Layout Selector Button'. */
  'aria-label'?: string
  onChange(item: T): void
}

export const MenuButtonSelector = <T extends MenuButtonItem>(props: MenuButtonSelector<T>) => {
  const {
    items,
    selected,
    onChange,
    showIcon = true,
    'aria-label': ariaLabel = 'Layout Selector Button',
    ...restProps
  } = props

  const { isSmallOrSmaller } = useDimensions()

  // Only drives the expand/collapse icon; the menu itself is uncontrolled.
  const [open, setOpen] = useState(false)

  return (
    <Menu onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <Button
            {...restProps}
            className={cn(moduleStyles.button, restProps.className)}
            disabled={items.length <= 1}
            startIcon={showIcon && selected?.Icon ? <selected.Icon /> : undefined}
            endIcon={open ? <ExpandLess /> : <ExpandMore />}
            aria-label={ariaLabel}
          >
            {isSmallOrSmaller ? '' : selected?.name}
          </Button>
        }
      />
      <MenuContent>
        {items.map(item => (
          <MenuItem key={item.name} disabled={item?.name === selected?.name} onClick={() => onChange(item)}>
            <ListItemIcon>{<item.Icon />}</ListItemIcon>
            {item.name}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  )
}

export default MenuButtonSelector
